import { isValidUsPhone } from './labCheckoutUtils';
import { buildPatientAddress, isPatientAddressComplete } from './patientAddress';

export function getLabBasePrice(lab, selectedMethod) {
  if (selectedMethod?.patient_price != null) return selectedMethod.patient_price;
  return lab?.display_price ?? lab?.price ?? 0;
}

export function getLabDiscountAmount(appliedCoupon, basePrice) {
  if (!appliedCoupon) return 0;
  if (appliedCoupon.label?.includes('%')) {
    return basePrice * (parseFloat(appliedCoupon.label) / 100 || 0);
  }
  return Math.min(parseFloat(appliedCoupon.label?.replace(/[^0-9.]/g, '') || '0') || 0, basePrice);
}

export function resolveSelectedLabMethod({ lab, result, methodHint }) {
  if (!result?.is_available || !methodHint) return null;
  const byMethod = (candidate) => (
    candidate?.method === methodHint.method ||
    (candidate?.assignment_id && candidate.assignment_id === methodHint.assignment_id)
  );
  if (result.is_combined) {
    const matched = result.serviceable_methods?.find(byMethod);
    return matched
      ? lab?.collectionOptions?.find(option => byMethod(option)) || matched
      : null;
  }
  return lab?.collectionOptions?.find(byMethod) || lab?.collectionOptions?.[0] || null;
}

export function getStep3Readiness({ selectedMethod, availResult, address, selectedDrawSite, zip }) {
  const selectedMethodAvailability = selectedMethod && availResult?.is_combined
    ? availResult.serviceable_methods?.find(m => m.method === selectedMethod.method)
    : null;
  const selectedMethodDrawSites = selectedMethodAvailability?.drawSites || availResult?.drawSites || [];
  const needsDrawSite = Boolean(
    selectedMethod &&
    ['walk_in_test', 'on_site_collection'].includes(selectedMethod.method) &&
    selectedMethodDrawSites.length > 0
  );

  const stateMatches = (availResult?.resolved_state && address?.state)
    ? availResult.resolved_state.trim().toUpperCase() === address.state.trim().toUpperCase()
    : true;
  const zipMatches = (zip && address?.zip)
    ? zip.trim() === address.zip.trim()
    : true;

  const canProceed = Boolean(
    selectedMethod &&
    availResult?.is_available &&
    stateMatches &&
    zipMatches &&
    isPatientAddressComplete(address) &&
    (!needsDrawSite || selectedDrawSite)
  );

  return {
    selectedMethodAvailability,
    selectedMethodDrawSites,
    needsDrawSite,
    canProceed,
    stateMatches,
    zipMatches,
  };
}

export function validateStep3Collection({ selectedMethod, availResult, address, selectedDrawSite, zip }) {
  if (!availResult) {
    return {
      isValid: false,
      error: 'Please enter your ZIP code and check availability before continuing.',
    };
  }
  // Stale-avail: top-level ZIP was changed after availability was last checked
  if (availResult.checkedZip && zip && availResult.checkedZip.trim() !== zip.trim()) {
    return {
      isValid: false,
      error: `Please re-check availability for ZIP code ${zip.trim()} before continuing.`,
    };
  }
  if (!availResult.is_available) {
    return {
      isValid: false,
      error: availResult.reason || 'This lab is not available in your area.',
    };
  }
  if (!selectedMethod) {
    return {
      isValid: false,
      error: 'Please select a collection method.',
    };
  }
  if (!isPatientAddressComplete(address)) {
    return {
      isValid: false,
      error: 'Please complete your patient address.',
    };
  }
  if (
    availResult.resolved_state &&
    address?.state &&
    availResult.resolved_state.trim().toUpperCase() !== address.state.trim().toUpperCase()
  ) {
    const addrState = address.state.trim().toUpperCase();
    const areaState = availResult.resolved_state.trim().toUpperCase();
    return {
      isValid: false,
      error: `Patient address state (${addrState}) does not match the service area state (${areaState}). Please enter an address in ${areaState} or update your ZIP code above.`,
    };
  }
  if (zip && address?.zip && zip.trim() !== address.zip.trim()) {
    return {
      isValid: false,
      error: `Patient address ZIP code (${address.zip.trim()}) does not match the service area ZIP code (${zip.trim()}). Please enter an address matching your service area ZIP code or update your ZIP code above.`,
    };
  }
  const readiness = getStep3Readiness({
    selectedMethod,
    availResult,
    address,
    selectedDrawSite,
    zip,
  });
  if (readiness.needsDrawSite && !selectedDrawSite) {
    return {
      isValid: false,
      error: 'Please select a draw site.',
    };
  }
  if (!readiness.canProceed) {
    return {
      isValid: false,
      error: 'Please complete all requirements for Step 3.',
    };
  }
  return {
    isValid: true,
    error: null,
  };
}

export function validateStep2Details({ email, phone, dob, sex }) {
  return Boolean(email && dob && sex && isValidUsPhone(phone));
}

export function buildLabCheckoutPayload({
  lab,
  patientId,
  dob,
  sex,
  phone,
  address,
  zip,
  selectedMethod,
  selectedDrawSite,
  aoeQuestions,
  aoeAnswers,
  consentHipaa,
  consentTerms,
  consentTele,
  paymentData,
  appliedCoupon,
  total,
  availabilityFingerprint,
}) {
  const normalizedAoeAnswers = (aoeQuestions || [])
    .map(q => ({
      marker_id: q.markerId,
      question_id: q.questionId,
      answer: aoeAnswers[`${q.markerId}:${q.questionId}`] || '',
    }))
    .filter(a => String(a.answer).trim());

  const methodAddress = buildPatientAddress(address, zip);

  return {
    patientId,
    patientDetails: { dob, sexAtBirth: sex, phone },
    address: methodAddress,
    aoeAnswers: normalizedAoeAnswers,
    consents: { hipaa: consentHipaa, terms: consentTerms, telehealth: consentTele },
    payment: paymentData,
    couponCode: appliedCoupon?.code || undefined,
    total,
    ...(availabilityFingerprint ? { availabilityFingerprint } : {}),
    collection: {
      method: selectedMethod.method,
      assignmentId: selectedMethod.assignment_id || undefined,
      zip,
      radius: '25',
      selectedSite: selectedDrawSite || undefined,
    },
    ...(lab?.is_combined && selectedMethod?.assignment_id
      ? { selectedAssignmentId: selectedMethod.assignment_id }
      : {}),
  };
}

export function mapLabSubmitError(err) {
  const steps = err.steps || [];
  if (steps.includes('junction_submission_failed')) {
    return {
      confirmedOrder: {
        state: 'junction_failed',
        orderNumber: err.orderNumber || '',
        message: err.detail || err.message || 'Your lab order could not be submitted. Please contact support.',
        total: 0,
      },
    };
  }
  if (steps.includes('junction_order_submitted') && !steps.includes('payment_captured')) {
    return {
      confirmedOrder: {
        state: 'payment_failed',
        orderNumber: err.orderNumber || '',
        message: 'Your lab order was created but payment could not be captured. Do NOT reorder — contact support.',
        total: 0,
      },
    };
  }
  // A gateway can legitimately report an "authorization" or tokenization
  // error.  Do not discard the checkout state unless the API actually
  // rejected the patient's credentials.
  if (err?.status === 401) {
    return {
      sessionExpired: true,
      message: 'Your session expired. Please sign in again. Your checkout progress was preserved.',
    };
  }
  return { message: err.message || 'An error occurred during checkout.' };
}
